import { townOfPostcode, townOfOutcode } from "../data/towns";

// postcodes.io (free, no key): postcode and district lookups for the
// search page, called from the browser.
const BASE_URL = "https://api.postcodes.io";
// The full postcodes nearest an outcode's centre, used to name its town
// (the same numbers as the Worker).
const NEARBY_POSTCODES = 10;
const NEARBY_RADIUS_M = 2000;

const FULL_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/;
const OUTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/;

export function normalisePostcode(text) {
  const compact = String(text || "").toUpperCase().replace(/\s+/g, "");
  // Full postcodes get their space back: "S12AB" -> "S1 2AB".
  return compact.length >= 5 && FULL_POSTCODE_RE.test(compact)
    ? `${compact.slice(0, -3)} ${compact.slice(-3)}`
    : compact;
}

// Read the text first, then parse, so a bad reply is logged, not thrown.
async function getJson(path) {
  try {
    const res = await fetch(`${BASE_URL}${path}`);
    const text = await res.text();
    if (!res.ok) return null; // 404 = not a real postcode
    try {
      return text ? JSON.parse(text) : null;
    } catch {
      console.error(`postcodes.io returned non-JSON for ${path}: ${text.slice(0, 200)}`);
      return null;
    }
  } catch (e) {
    console.error(`postcodes.io request failed for ${path}`, e);
    return null;
  }
}

// "S1" or "S1 2AB" -> { postcode, lat, lng, places: ["Sheffield"] }, or null.
export async function lookupPostcode(text) {
  const postcode = normalisePostcode(text);
  if (FULL_POSTCODE_RE.test(postcode)) {
    const data = await getJson(`/postcodes/${encodeURIComponent(postcode)}`);
    const r = data && data.result;
    if (!r) return null;
    return { postcode: r.postcode, lat: r.latitude, lng: r.longitude, places: [r.admin_district].filter(Boolean) };
  }
  if (OUTCODE_RE.test(postcode)) {
    const data = await getJson(`/outcodes/${encodeURIComponent(postcode)}`);
    const r = data && data.result;
    if (!r) return null;
    return { postcode: r.outcode, lat: r.latitude, lng: r.longitude, places: r.admin_district || [] };
  }
  return null;
}

// Many postcodes at once (the CSV upload): full postcodes in bulk requests of
// up to 100, districts one by one. Returns { "S1 2AB": { postcode, lat, lng, places } };
// a postcode missing from it wasn't found.
export async function lookupPostcodes(codes) {
  const found = {};
  const full = codes.filter(c => FULL_POSTCODE_RE.test(c));
  const others = codes.filter(c => !FULL_POSTCODE_RE.test(c));
  for (let i = 0; i < full.length; i += 100) {
    const batch = full.slice(i, i + 100);
    try {
      const res = await fetch(`${BASE_URL}/postcodes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postcodes: batch }),
      });
      const data = res.ok ? await res.json() : null;
      for (const entry of (data && data.result) || []) {
        const r = entry && entry.result;
        if (r) found[entry.query] = { postcode: entry.query, lat: r.latitude, lng: r.longitude, places: [r.admin_district].filter(Boolean) };
      }
    } catch (e) {
      console.error("postcodes.io bulk lookup failed", e);
    }
  }
  await Promise.all(others.map(async code => {
    const place = await lookupPostcode(code);
    if (place) found[code] = place;
  }));
  return found;
}

// "HD3" -> "Huddersfield", "S1 2LF" -> "Sheffield", or null. The town a
// business owner would recognise, by the same rule the Worker uses when it
// saves a scan (core/data/towns.js). Used for jobs saved before towns were.
export async function lookupTown(text) {
  const postcode = normalisePostcode(text);
  if (FULL_POSTCODE_RE.test(postcode)) {
    const data = await getJson(`/postcodes/${encodeURIComponent(postcode)}`);
    return townOfPostcode(data && data.result);
  }
  if (OUTCODE_RE.test(postcode)) {
    const data = await getJson(`/outcodes/${encodeURIComponent(postcode)}`);
    const r = data && data.result;
    if (!r) return null;
    const near = await getJson(`/postcodes?lon=${r.longitude}&lat=${r.latitude}&limit=${NEARBY_POSTCODES}&radius=${NEARBY_RADIUS_M}`);
    return townOfOutcode(postcode, near && near.result, r.admin_district);
  }
  return null;
}

// Suggestions while typing: the district itself if it's real ("S1"),
// then full postcodes that start with what's typed.
export async function suggestPostcodes(text, limit = 5) {
  const typed = normalisePostcode(text);
  if (typed.length < 2) return [];
  const [district, full] = await Promise.all([
    OUTCODE_RE.test(typed) ? lookupPostcode(typed) : Promise.resolve(null),
    getJson(`/postcodes/${encodeURIComponent(typed)}/autocomplete?limit=${limit}`),
  ]);
  const suggestions = [];
  if (district) suggestions.push({ postcode: district.postcode, hint: district.places[0] || "" });
  for (const pc of (full && full.result) || []) suggestions.push({ postcode: pc, hint: "" });
  return suggestions.slice(0, limit + 1);
}
